import type { OrderData, OrderLineItemData } from "~/domains/orders/types/modelsV2";
import type { CaseDataObject } from "../orderDetails/components/ServiceCases/types";
import { Param, TooltipParam } from "../common/components/v2/Params";
import { useState } from "react";
import { useSendOrderFeedback } from "../orderDetails/hooks/useSendOrderFeedback/useSendOrderFeedback";
import { useTranslation } from "react-i18next";
import { ManualPackageRestrictionContext } from "~/features/PackageRestriction/PackageRestrictionContext";
import { fakeDescriptorFromProduct } from "~/domains/product/utils/productDescriptorUtils";
import { DateFormatType, formatNullableDate } from "~/common/utils/DateFormatter";
import { formatShippingOrDeliveryDate } from "~/common/utils/dateUtils";
import { OrderLineItemElements } from "../common/components/OrderLineItemElements";
import { getPackagingLabelAtLineItem } from "~/features/cart/cart/utils/cartUtils";
import {
  ContainerStack,
  OrderLineSummary,
  OrderLineSummaryContainer,
} from "../common/components/v2/OrderAndQuoteSharedStyles";
import { hasSuccessor } from "~/utils/productUtils";
import { NewVersionMessage } from "~/common/components/Messages/NewVersionMessage";
import { getQuoteNotaFiscalData } from "../orderList/components/OrderLineTable";
import OrderSubLineList from "./OrderSubLineList";
import FileDownloadPopup from "./FileDowloadPopup";
import OrderLineHeaderSection from "./OrderLineHeader";
import ServiceCasesSection from "./ServiceCasesSection";
import { Stack, Typography } from "~/common/mui";
import {
  renderHeaderDeliverDate,
  renderHeaderNetValue,
  renderHeaderQuantity,
} from "../orderDetailsV2/OrderDetailsV2/renderValues4HeaderOrdersV2";
import { ProductLineGrid } from "~/features/cart/cartV2/ProductLineGrid";
import { PriceElements } from "~/domains/pricing/components/Price/PriceV4";
import { OrderTableProductV2 } from "../orderDetailsV2/OrderDetailsV2/OrderTableProductV2";
import { TrackerV2 } from "../orderDetailsV2/OrderDetailsV2/TrackerV2";
import { translateWarehouseName } from "~/utils/language/translationUtils";

export interface RowProps {
  data: OrderLineItemData;
  orderData: OrderData;
  lineItemIndex: number;
  soNumber?: string;
  poNumber?: string;
  selectable?: boolean;
  selectionDisabled?: boolean;
  selected?: boolean;
  selectItem?: (itemIndex: number) => void;
  selectableIndexSet?: Set<number>;
  serviceCasesOverride?: CaseDataObject[];
}

const OrderLineListRow = ({
  data,
  orderData,
  lineItemIndex,
  soNumber,
  poNumber,
  selectable,
  selectionDisabled,
  selected,
  selectItem,
  selectableIndexSet,
  serviceCasesOverride,
}: RowProps) => {
  const { t } = useTranslation();
  const [showDownload, setShowDownload] = useState(false);

  const serviceCases = serviceCasesOverride ?? orderData.serviceCases?.[data.lineNo] ?? [];

  const [snackbarData, setSnackbarData] = useState<{
    open: boolean;
    message: string;
  }>({
    open: false,
    message: "",
  });

  const [popups, open, userCreateCase] = useSendOrderFeedback(
    data,
    orderData,
    lineItemIndex,
    soNumber,
    poNumber,
    setSnackbarData,
  );

  const openDownload = () => setShowDownload(true);
  const closeDownload = () => setShowDownload(false);

  const haveSubEntries = data.subEntries && data?.subEntries?.length > 0;

  const getQuoteNotaFiscalArray = getQuoteNotaFiscalData(t, data);

  const showTrackerInfo =
    !!data.invoiceData &&
    (data.invoiceData.trackingNo !== "-" ||
      (data.invoiceData.shippingPartnerName && data.invoiceData.shippingPartnerName !== "-") ||
      (data.invoiceData.shippingPartnerId && data.invoiceData.shippingPartnerId !== "-"));

  const quantitySection = haveSubEntries ? (
    renderHeaderQuantity(data, t)
  ) : (
    <TooltipParam
      label={t("quoteListPage.table.header.confirmedQuantity")}
      value={t("common.of.text.with.values", {
        firstValue: data.confirmedQty,
        secondValue: data.qty,
      })}
      tooltipContent={
        <Stack gap={0.5}>
          <Param label={t("quoteListPage.table.header.confirmedQuantity")} value={data.confirmedQty} />
          <Param label={t("quoteListPage.table.header.requestedQuantity")} value={data.qty} />
        </Stack>
      }
    />
  );

  const deliverySection = haveSubEntries ? (
    renderHeaderDeliverDate(data, t)
  ) : (
    <TooltipParam
      label={t("quoteListPage.table.header.estimatedDeliveryDate")}
      value={formatShippingOrDeliveryDate(data.deliveryDate, DateFormatType.DATE)}
      footer={formatNullableDate(data.requestedDeliveryDate, DateFormatType.DATE)}
      tooltipContent={
        <Stack gap={0.5}>
          <Param
            label={t("quoteListPage.table.header.estimatedDeliveryDate")}
            value={formatShippingOrDeliveryDate(data.deliveryDate, DateFormatType.DATE)}
          />

          <Param
            label={t("quoteListPage.table.header.reqDeliveryDate")}
            value={formatNullableDate(data.requestedDeliveryDate, DateFormatType.DATE)}
          />
        </Stack>
      }
    />
  );

  return (
    <ManualPackageRestrictionContext restrictedPackNo={data.packCode}>
      <ContainerStack>
        <OrderLineHeaderSection
          data={data}
          selectable={selectable}
          selectionDisabled={selectionDisabled}
          selected={selected}
          selectableIndexSet={selectableIndexSet}
          lineItemIndex={lineItemIndex}
          selectItem={selectItem}
          onDownload={openDownload}
        />
        <OrderLineSummaryContainer>
          <OrderLineSummary>
            <Param
              label={t("orderDetailsPage.table.header.product")}
              value={<OrderTableProductV2 product={data.product} />}
            />

            {quantitySection}

            {deliverySection}

            <Stack>
              <ProductLineGrid.NetPrice>
                <Param
                  label={t("orderDetailsPage.table.header.price")}
                  value={<PriceElements.Price variant="bodyLarge" color="textPrimary" price={data.netPrice} />}
                  footer={
                    <PriceElements.NetPriceLabel
                      labelVariant="short"
                      variant="bodySmall"
                      color="text.secondary"
                      price={data.netPrice}
                    />
                  }
                />
              </ProductLineGrid.NetPrice>
            </Stack>

            {haveSubEntries ? (
              <Param value={renderHeaderNetValue(data, t)} />
            ) : (
              <Param
                label={t("orderListPage.table.header.total")}
                value={
                  <Typography variant="bodyLarge" color="textPrimary">
                    <OrderLineItemElements.TotalPrice data={data} />
                  </Typography>
                }
              />
            )}

            <Param label={t("orderDetails.Schaeffler.id.title")} value={data.product.sapMaterialNumber} />
            <Param
              label={t("quoteDetailsPage.summary.packagingCode")}
              value={getPackagingLabelAtLineItem({
                t,
                productDescriptor: fakeDescriptorFromProduct(data.product),
                packCode: data.packCode,
                packSize: data.packSize,
                quantity: haveSubEntries ? data.subEntries?.[0].qty ?? data.qty : data.qty,
              })}
              footer={translateWarehouseName(t, data.location)}
            />
            <Param label={t("orderDetails.customerMatetialId")} value={data.product.sapMaterialNumber} />
            <Stack>
              <ProductLineGrid.PriceReference>
                <Param label={t("cartPage.table.header.priceReference.title")} value={data.priceReference || "-"} />
              </ProductLineGrid.PriceReference>
            </Stack>
            {!haveSubEntries ? (
              <>
                {showTrackerInfo && data.invoiceData && (
                  <Param
                    label={t("quoteDetailsPage.summary.shipmentReference")}
                    value={<TrackerV2 data={data.invoiceData} />}
                  />
                )}
              </>
            ) : (
              <>
                {getQuoteNotaFiscalArray.length > 0 &&
                  getQuoteNotaFiscalArray[0].map(({ label, value }) => {
                    return <Param label={label} value={value} />;
                  })}
              </>
            )}
          </OrderLineSummary>
          {hasSuccessor(data.product) && <NewVersionMessage baseProduct={data.product} />}
        </OrderLineSummaryContainer>
        {data.subEntries && data.subEntries?.length > 0 && (
          <OrderSubLineList
            data={data}
            orderData={orderData}
            lineItemIndex={lineItemIndex}
            onCloseDownload={() => setShowDownload(false)}
            showDownload={showDownload}
            onOpenDownload={openDownload}
          />
        )}
        <ServiceCasesSection
          serviceCases={serviceCases}
          userCreateCase={userCreateCase}
          open={open}
          lineNo={data.lineNo}
          soNumber={soNumber}
          snackbarData={snackbarData}
          setSnackbarData={setSnackbarData}
          popups={popups}
        />
      </ContainerStack>
      {showDownload && data.invoiceData && (
        <FileDownloadPopup invoiceData={data.invoiceData} onCloseDownload={closeDownload} />
      )}
    </ManualPackageRestrictionContext>
  );
};

export default OrderLineListRow;
